// Slow-rotating equirect skybox behind the TV dashboard.
//
// Deliberately raw three.js rather than react-three-fiber: this app carries
// two three instances (a WebXR emulation dep bundles its own), and R3F's
// reconciler silently refused to render children inside this particular
// Canvas. An imperative sphere has no reconciler to disagree with.

import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const TVSkyBackdrop: React.FC<{ url: string }> = ({ url }) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(host.clientWidth, host.clientHeight);
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(68, host.clientWidth / host.clientHeight, 0.1, 200);
    camera.position.set(0, 0, 0.1);

    const geo = new THREE.SphereGeometry(60, 48, 32);
    const mat = new THREE.MeshBasicMaterial({ side: THREE.BackSide, toneMapped: false });
    const sphere = new THREE.Mesh(geo, mat);
    scene.add(sphere);

    let disposed = false;
    new THREE.TextureLoader().load(url, (tex) => {
      if (disposed) { tex.dispose(); return; }
      tex.colorSpace = THREE.SRGBColorSpace;
      mat.map = tex;
      mat.needsUpdate = true;
    }, undefined, (err) => console.error('[tv-sky] texture failed', url, err));

    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      sphere.rotation.y += dt * 0.006;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      camera.aspect = host.clientWidth / host.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(host.clientWidth, host.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      geo.dispose();
      mat.map?.dispose();
      mat.dispose();
      renderer.dispose();
      host.removeChild(renderer.domElement);
    };
  }, [url]);

  return <div ref={hostRef} className="tvd-bg" />;
};

export default TVSkyBackdrop;
